import { lineMeta } from "../../lib/scopeLines";

/**
 * The crew's do-not-perform list (Oct 30, 2026, the scope gate): the carrier
 * lines the homeowner kept, as the RM ticked them on the carrier's scope. No
 * money: the crew needs to know what not to touch, not what it's worth.
 * Mounted on the PM Board workbook (from the deal's picks) and on the PM's job
 * sheet (pm_board_jobs.do_not_perform). Renders nothing when there are none.
 */

export interface DoNotPerformItem {
  n?: string | null;
  area?: string | null;
  description: string;
  quantity?: number | null;
  unit?: string | null;
}

const text = (v: unknown): string | null => {
  const s = v == null ? "" : String(v).replace(/\s+/g, " ").trim();
  return s || null;
};

/** The PM board's do_not_perform rows (or the deal's picks), read defensively:
 *  null or an older row with none reads as an empty list. */
export function readDoNotPerform(v: unknown): DoNotPerformItem[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((r): r is Record<string, unknown> => !!r && typeof r === "object")
    .map((r) => {
      const q = r.quantity == null || r.quantity === "" ? null : Number(r.quantity);
      return {
        n: text(r.n),
        area: text(r.area),
        description: text(r.description) ?? "",
        quantity: q != null && Number.isFinite(q) ? q : null,
        unit: text(r.unit),
      };
    })
    .filter((r) => r.description);
}

export default function DoNotPerformCard({
  items,
  rmName,
  className = "",
}: {
  items: DoNotPerformItem[];
  /** The deal's RM, named in the "call first" line. */
  rmName?: string | null;
  className?: string;
}) {
  if (items.length === 0) return null;
  const who = rmName && rmName !== "—" ? rmName : "the RM";
  return (
    <div className={`rounded-lg border border-rose-300 bg-rose-50 p-3 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-rose-800">🚫 Don't do this work</div>
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-200 text-rose-900">
          {items.length} {items.length === 1 ? "item" : "items"}
        </span>
      </div>
      <div className="mt-0.5 text-xs text-rose-800">The homeowner kept these items from the carrier's scope. Bardin isn't doing them.</div>
      <ul className="mt-2 space-y-1">
        {items.map((it, i) => {
          const meta = lineMeta({ area: it.area ?? null, quantity: it.quantity ?? null, unit: it.unit ?? null });
          return (
            <li key={i} className="rounded bg-white border border-rose-100 px-2.5 py-1.5 text-sm text-slate-800">
              {it.n && <span className="mr-1.5 font-mono text-[11px] text-rose-700">#{it.n}</span>}
              {it.description}
              {meta && <span className="block text-xs text-slate-500">{meta}</span>}
            </li>
          );
        })}
      </ul>
      <div className="mt-2 text-xs font-medium text-rose-900">
        If the homeowner asks for any of these on build day, call {who} first. It changes the price and needs a written change order.
      </div>
    </div>
  );
}
